import { ReactNode, useMemo } from "react";

export type TableEmptyViewVariant =
    | "loading"
    | "filtered"
    | "connect"
    | "no-data"
    | "none";

export type RenderableTableEmptyViewVariant = Exclude<
    TableEmptyViewVariant,
    "loading" | "none"
>;

interface TableEmptyViewState {
    isEmpty: boolean;
    hasConnectedAccounts: boolean;
    hasRecordsInWorkspace: boolean;
    isCheckingWorkspaceRecords: boolean;
}

// Without a provider, show connect immediately; probe may still upgrade to filtered.
export function resolveTableEmptyViewVariant({
    isEmpty,
    hasConnectedAccounts,
    hasRecordsInWorkspace,
    isCheckingWorkspaceRecords,
}: TableEmptyViewState): TableEmptyViewVariant {
    if (!isEmpty) {
        return "none";
    }
    if (hasRecordsInWorkspace) {
        return "filtered";
    }
    if (!hasConnectedAccounts) {
        return "connect";
    }
    if (isCheckingWorkspaceRecords) {
        return "loading";
    }
    return "no-data";
}

interface UseTableEmptyViewParams extends TableEmptyViewState {
    renderEmptyView: (variant: RenderableTableEmptyViewVariant) => ReactNode;
    footerHiddenVariants?: readonly RenderableTableEmptyViewVariant[];
}

const DEFAULT_FOOTER_HIDDEN_VARIANTS: readonly RenderableTableEmptyViewVariant[] =
    ["connect"];

export function useTableEmptyView({
    isEmpty,
    hasConnectedAccounts,
    hasRecordsInWorkspace,
    isCheckingWorkspaceRecords,
    renderEmptyView,
    footerHiddenVariants = DEFAULT_FOOTER_HIDDEN_VARIANTS,
}: UseTableEmptyViewParams) {
    const variant = useMemo(
        () =>
            resolveTableEmptyViewVariant({
                isEmpty,
                hasConnectedAccounts,
                hasRecordsInWorkspace,
                isCheckingWorkspaceRecords,
            }),
        [
            hasConnectedAccounts,
            hasRecordsInWorkspace,
            isCheckingWorkspaceRecords,
            isEmpty,
        ],
    );

    const emptyView = useMemo(
        () =>
            variant === "loading" || variant === "none"
                ? undefined
                : () => renderEmptyView(variant),
        [renderEmptyView, variant],
    );

    const shouldHideFooter = footerHiddenVariants.some(
        (hiddenVariant) => hiddenVariant === variant,
    );

    return { emptyView, shouldHideFooter, variant };
}
